// pages/HomePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
    Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Skeleton,
  Paper,
  Fade,
  Grow,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Avatar,
  Snackbar,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Restaurant as RestaurantIcon,
  DeliveryDining as DeliveryIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingIcon,
  LocalOffer as OfferIcon,
  EmojiEvents as AwardIcon,
  Google as GoogleIcon,
  Store as StoreIcon,
  Storefront as StorefrontIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  WatchLater as WatchLaterIcon,
  QrCodeScanner as QrCodeIcon,
  PointOfSale as PosIcon,
  TableRestaurant as TableRestaurantIcon,
  Analytics as AnalyticsIcon,
  CloudSync as SyncIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
  Inventory as InventoryIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  SupportAgent as SupportAgentIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import WelcomePopup from '../components/WelcomePopup';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import GoogleMapModal from '../components/GoogleMapModal';
import currencyTrans from '../components/CarrancyTrans';
import { useAuth } from '../context/AuthContext';
import noPhoto from "../assets/no_photo.webp";
import { motion } from 'framer-motion';

const HomePage = ({ keywordsData, language }) => {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { user, login } = useAuth();
  
  // Location states
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [location, setLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [openMap, setOpenMap] = useState(false);
  const [isSelectMap, setIsSelectMap] = useState(false);
  const [openWelcomeModel, setOpenWelcomeModel] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Restaurant states
  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cuisineTypes, setCuisineTypes] = useState([]);
  const [selectedCuisine, setSelectedCuisine] = useState('all');
  const [gpsChecking, setGpsChecking] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Recently viewed stores
  const [recentStores, setRecentStores] = useState([]);

// Inside your component
const [videoDialogOpen, setVideoDialogOpen] = useState(false);

// YouTube video URL (replace with your actual video URL)
const videoUrl = 'https://www.youtube.com/embed/TZC5n1UB-QM?si=9hOTAiG0oelZ6FBN'; // Replace with your video ID

const handleOpenVideo = () => {
  setVideoDialogOpen(true);
};

const handleCloseVideo = () => {
  setVideoDialogOpen(false);
};
  // Feature cards data
  const features = [
    {
      icon: <QrCodeIcon sx={{ fontSize: 40 }} />,
      title: 'QR Code Ordering',
      description: 'مسح QR كود لعرض المنيو والطلب مباشرة',
      color: '#FF6B6B',
      gradient: 'linear-gradient(135deg, #FF6B6B20, #FF6B6B05)',
    },
    {
      icon: <PosIcon sx={{ fontSize: 40 }} />,
      title: 'POS System',
      description: 'نظام نقاط بيع متكامل إدارة المبيعات والمخزون',
      color: '#4ECDC4',
      gradient: 'linear-gradient(135deg, #4ECDC420, #4ECDC405)',
    },
    {
      icon: <TableRestaurantIcon sx={{ fontSize: 40 }} />,
      title: 'Smart Table Management',
      description: 'إدارة الطاولات وتتبع الطلبات بكفاءة',
      color: '#FFD93D',
      gradient: 'linear-gradient(135deg, #FFD93D20, #FFD93D05)',
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40 }} />,
      title: 'Advanced Analytics',
      description: 'تقارير متقدمة للمبيعات والأرباح والأداء',
      color: '#6C5CE7',
      gradient: 'linear-gradient(135deg, #6C5CE720, #6C5CE705)',
    },
    {
      icon: <SyncIcon sx={{ fontSize: 40 }} />,
      title: 'Cloud Sync',
      description: 'مزامنة فورية بين المنصة ونظام نقاط البيع',
      color: '#A8E6CF',
      gradient: 'linear-gradient(135deg, #A8E6CF20, #A8E6CF05)',
    },
    {
      icon: <PrintIcon sx={{ fontSize: 40 }} />,
      title: 'Smart Printing',
      description: 'طباعة ذكية حسب الأقسام مع دعم طابعات متعددة',
      color: '#FF8A80',
      gradient: 'linear-gradient(135deg, #FF8A8020, #FF8A8005)',
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 40 }} />,
      title: 'Multiple Payments',
      description: 'طرق دفع متعددة مع خصومات ورسوم مرنة',
      color: '#81D4FA',
      gradient: 'linear-gradient(135deg, #81D4FA20, #81D4FA05)',
    },
    {
      icon: <InventoryIcon sx={{ fontSize: 40 }} />,
      title: 'Inventory Management',
      description: 'إدارة المخزون مع تنبيهات للمنتجات منخفضة المخزون',
      color: '#FFCC80',
      gradient: 'linear-gradient(135deg, #FFCC8020, #FFCC8005)',
    },
  ];

  const stats = [
    { value: '500+', label: 'متاجر تستخدم المنصة' },
    { value: '50K+', label: 'طلب شهري' },
    { value: '98%', label: 'رضا العملاء' },
    { value: '24/7', label: 'دعم فني' },
  ];

  // Add Snackbar close handler
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Load recently viewed stores from localStorage
  useEffect(() => {
    const savedRecentStores = localStorage.getItem('recent_stores');
    if (savedRecentStores) {
      try {
        const parsed = JSON.parse(savedRecentStores);
        setRecentStores(parsed.slice(0, 10));
      } catch (e) {
        console.error('Error loading recent stores:', e);
      }
    }
  }, []);

  // Save store to recently viewed
  const addToRecentStores = (store) => {
    setRecentStores(prev => {
      const filtered = prev.filter(s => s.id !== store.id);
      const newRecent = [store, ...filtered].slice(0, 10);
      localStorage.setItem('recent_stores', JSON.stringify(newRecent));
      return newRecent;
    });
  };

  // Clear recently viewed stores
  const clearRecentStores = () => {
    setRecentStores([]);
    localStorage.removeItem('recent_stores');
  };

  // Remove single store from recent
  const removeFromRecent = (storeId, e) => {
    e.stopPropagation();
    setRecentStores(prev => {
      const newRecent = prev.filter(s => s.id !== storeId);
      localStorage.setItem('recent_stores', JSON.stringify(newRecent));
      return newRecent;
    });
  };

  // Google Login handler
  const handleGoogleLogin = async (response) => {
    setGoogleLoading(true);
    try {
      const res = await api.post('/auth/google', {
        credential: response.credential,
        client_id: '387866063920-1bfr2kigso4o76ft4nh2fr3i5m61elee.apps.googleusercontent.com'
      });
      
      if (res.data.success) {
        const { token, user } = res.data;
        await login(token, user);
      }
    } catch (error) {
      console.error('Google login error:', error);
    } finally {
      setGoogleLoading(false);
    }
  };

  // Initialize Google One-Tap
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && !user) {
        window.google.accounts.id.initialize({
          client_id: '387866063920-1bfr2kigso4o76ft4nh2fr3i5m61elee.apps.googleusercontent.com',
          callback: handleGoogleLogin,
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signin'
        });
        
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed()) {
            console.log('One-tap not displayed:', notification.getNotDisplayedReason());
          }
        });
      }
    };
    document.body.appendChild(script);
    
    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
      document.body.removeChild(script);
    };
  }, [user]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Google Maps API
  const loadGoogleMapsAPI = (callback) => {
    if (window.google && window.google.maps) {
      callback();
      return;
    }
  
    if (!document.getElementById("google-maps-script")) {
      const script = document.createElement("script");
      const api = process.env.REACT_APP_MAP_API_KEY;
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${api}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google Maps API Loaded");
        callback();
      };
      document.body.appendChild(script);
    } else {
      setTimeout(() => {
        if (window.google && window.google.maps) {
          callback();
        }
      }, 1000);
    }
  };

  // Fetch places for autocomplete
  const fetchPlaces = async (input) => {
    if (isSelectMap) return;
    
    try {
      const response = await api.get(`/autocomplete?input=${input}&language=${language}`);
      const data = response.data;
      if (data.predictions) {
        setSuggestions(data.predictions.map((place) => place.description));
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching places:', error);
    }
  };

  // Handle location selection from search
  const handleLocationSelect = (locationData) => {
    if (!window.google || !window.google.maps) {
      loadGoogleMapsAPI(() => handleLocationSelect(locationData));
      return;
    }

    if (locationData.address) {
      setQuery(locationData.address);
      localStorage.setItem('user_location', JSON.stringify(locationData));
      setLocation({
        lat: locationData.lat,
        lng: locationData.lng,
        address: locationData.address
      });
      fetchNearbyRestaurants(locationData.lat, locationData.lng);
      setIsSelectMap(true);
      setShowSuggestions(false);
    } else {
      setQuery(locationData);
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: locationData }, (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          setSelectedLocation({
            lat: loc.lat(),
            lng: loc.lng(),
            address: locationData,
          });
          setOpenMap(true);
        }
      });
      setShowSuggestions(false);
    }
    setSuggestions([]);
  };

  const checkGpsAndOpenMap = () => {
    if (!navigator.geolocation) {
      setSnackbar({
        open: true,
        message: language === 'ar' 
          ? 'المتصفح الخاص بك لا يدعم تحديد الموقع الجغرافي'
          : 'Your browser does not support geolocation',
        severity: 'error'
      });
      return;
    }

    setGpsChecking(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsChecking(false);
        setSelectedLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setOpenMap(true);
      },
      (error) => {
        setGpsChecking(false);
        
        let errorMessage = '';
        let severity = 'warning';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = language === 'ar'
              ? '⚠️ تم رفض الوصول إلى الموقع. يرجى تمكين خدمات الموقع في إعدادات هاتفك أو المتصفح'
              : '⚠️ Location access denied. Please enable location services in your phone or browser settings';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = language === 'ar'
              ? '⚠️ معلومات الموقع غير متوفرة. يرجى التحقق من إعدادات GPS'
              : '⚠️ Location information unavailable. Please check your GPS settings';
            break;
          case error.TIMEOUT:
            errorMessage = language === 'ar'
              ? '⚠️ انتهى وقت محاولة تحديد الموقع. يرجى المحاولة مرة أخرى'
              : '⚠️ Location request timed out. Please try again';
            break;
          default:
            errorMessage = language === 'ar'
              ? '⚠️ يرجى تمكين خدمات الموقع (GPS) لتحديد موقعك الحالي بدقة'
              : '⚠️ Please enable location services (GPS) to detect your current location accurately';
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: severity
        });
      },
      { 
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleAutoDetect = () => {
    checkGpsAndOpenMap();
  };

  // Handle search input change
  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    clearTimeout(window.debounceTimeout);
    window.debounceTimeout = setTimeout(() => {
      if (value.length > 2) {
        fetchPlaces(value);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 500);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const fetchNearbyRestaurants = async (lat, lng) => {
    try {
      setLoading(true);
      const params = { lat, lng, radius: 10, sortBy: 'distance', page: 1, limit: 20 };
      const response = await api.get('/restaurants/nearby', { params });
      
      if (response.data.success) {
        setNearbyRestaurants(response.data.restaurants || []);
      }
    } catch (error) {
      console.error('Error fetching nearby restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToShopOwner = () => {
    navigate('/shop-owner-request');
  };

  // Handle restaurant click - save to recent stores
  const handleRestaurantClick = (restaurant) => {
    const storeData = {
      id: restaurant.id,
      name: restaurant.name,
      name_en: restaurant.name_en,
      logo: restaurant.logo || restaurant.cover_image,
      cuisine_type: restaurant.cuisine_type,
      rating: restaurant.rating
    };
    addToRecentStores(storeData);
    navigate(`/restaurant/${restaurant.id}`);
  };

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const savedLocation = localStorage.getItem('user_location');
        if (savedLocation) {
          const loc = JSON.parse(savedLocation);
          setLocation(loc);
          setQuery(loc.address);
          fetchNearbyRestaurants(loc.lat, loc.lng);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const getGridSize = () => {
    if (isMobile) return 12;
    if (isTablet) return 6;
    return 4;
  };

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', pb: { xs: 8, md: 10 } }}>
      {/* Hero Section with Platform Introduction */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 50%, #0D0D0D 100%)',
        borderBottom: '2px solid rgba(255,215,0,0.3)',
        py: { xs: 4, sm: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated Background Elements */}
        <Box sx={{
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '60%',
          height: '100%',
          background: 'radial-gradient(circle, rgba(255,215,0,0.05) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'pulse 8s ease-in-out infinite',
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '-30%',
          left: '-10%',
          width: '40%',
          height: '60%',
          background: 'radial-gradient(circle, rgba(255,215,0,0.03) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'pulse 6s ease-in-out infinite reverse',
        }} />

        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            {/* Platform Badge */}
            <Grow in timeout={800}>
              <Chip
                label={language === 'ar' ? '🚀 منصة QR Table المتكاملة' : '🚀 QR Table Integrated Platform'}
                sx={{
                  bgcolor: 'rgba(255,215,0,0.1)',
                  color: '#FFD700',
                  mb: 3,
                  px: 2,
                  py: 1,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  fontWeight: 700,
                  border: '1px solid rgba(255,215,0,0.2)',
                  '& .MuiChip-label': { fontWeight: 700 }
                }}
              />
            </Grow>

            {/* Main Title */}
            <Grow in timeout={1000}>
              <Typography 
                variant="h1" 
                sx={{ 
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #FFD700 40%, #FFA500 70%, #FFD700 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', sm: '2.8rem', md: '3.8rem' },
                  fontWeight: 900,
                  mb: 2,
                  textShadow: '0 2px 40px rgba(255, 215, 0, 0.1)',
                  letterSpacing: { xs: '0px', md: '2px' },
                  lineHeight: 1.2
                }}
              >
                {language === 'ar' 
                  ? 'ثورة في عالم المتاجر' 
                  : 'Revolutionizing the Store Industry'}
              </Typography>
            </Grow>

            {/* Subtitle */}
            <Fade in timeout={1200}>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: '#B3B3B3',
                  fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' },
                  fontWeight: 400,
                  mb: 3,
                  maxWidth: '700px',
                  mx: 'auto',
                  px: 2,
                  lineHeight: 1.8
                }}
              >
                {language === 'ar' 
                  ? 'منصة QR Table ونظام نقاط البيع المتكامل يجمعان بين الراحة والكفاءة لتجربة تسوق لا تُنسى'
                  : 'QR Table platform and integrated POS system combine convenience and efficiency for an unforgettable shoping experience'}
              </Typography>
            </Fade>

            {/* Stats */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 2, sm: 4 }, mb: 4, flexWrap: 'wrap' }}>
              {stats.map((stat, index) => (
                <Fade in timeout={1500 + index * 200} key={index}>
                  <Box sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                    <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 900, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B6B6B', fontWeight: 500, display: 'block', mt: 0.5 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </Fade>
              ))}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', px: 2 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setOpenWelcomeModel(true)}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#000000',
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  borderRadius: '50px',
                  fontWeight: 700,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  '&:hover': { 
                    bgcolor: '#FFE55C',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(255, 215, 0, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {language === 'ar' ? 'ابدأ الآن' : 'Get Started'}
              </Button>
              
      <Button
  variant="outlined"
  size="large"
  startIcon={<PlayArrowIcon />}
  onClick={handleOpenVideo}
  sx={{
    borderColor: '#FFD700',
    color: '#FFD700',
    px: { xs: 3, sm: 4 },
    py: { xs: 1, sm: 1.5 },
    borderRadius: '50px',
    fontWeight: 700,
    fontSize: { xs: '0.8rem', sm: '0.9rem' },
    '&:hover': {
      borderColor: '#FFE55C',
      backgroundColor: 'rgba(255, 215, 0, 0.1)',
      transform: 'translateY(-2px)'
    },
    transition: 'all 0.3s ease',
  }}
>
  {language === 'ar' ? 'شاهد الفيديو' : 'Watch Video'}
</Button>

              
              {!user && (
                <Button
                  variant="outlined"
                  startIcon={googleLoading ? <CircularProgress size={18} /> : <GoogleIcon />}
                  disabled={googleLoading}
                  onClick={() => window.google?.accounts?.id?.prompt()}
                  sx={{
                    borderColor: '#FFD700',
                    color: '#FFD700',
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    borderRadius: '50px',
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    '&:hover': {
                      borderColor: '#FFE55C',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {language === 'ar' ? 'تسجيل الدخول بجوجل' : 'Sign in with Google'}
                </Button>
              )}
              
              {(user && user.role === 'user') && (
                <Button
                  variant="outlined"
                  startIcon={<StorefrontIcon />}
                  onClick={handleUpgradeToShopOwner}
                  sx={{
                    borderColor: '#FFD700',
                    color: '#FFD700',
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    borderRadius: '50px',
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    '&:hover': {
                      borderColor: '#FFE55C',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {language === 'ar' ? 'الترقية إلى حساب متجر' : 'Upgrade to Store Account'}
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 4, sm: 6, md: 8 }, bgcolor: 'rgba(255,255,255,0.02)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5, md: 6 } }}>
            <Chip
              label={language === 'ar' ? '✨ المميزات' : '✨ Features'}
              sx={{
                bgcolor: 'rgba(255,215,0,0.1)',
                color: '#FFD700',
                mb: 2,
                fontWeight: 700
              }}
            />
            <Typography 
              variant="h3" 
              sx={{
                fontWeight: 900,
                mb: 2,
                background: 'linear-gradient(135deg, #FFFFFF, #FFD700)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' }
              }}
            >
              {language === 'ar' ? 'كل ما تحتاجه في منصة واحدة' : 'Everything You Need in One Platform'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#6B6B6B', maxWidth: 600, mx: 'auto', px: 2 }}>
              {language === 'ar' 
                ? 'منصة متكاملة تجمع بين نظام QR للطلب ونظام نقاط البيع المتقدم'
                : 'An integrated platform combining QR ordering system and advanced POS system'}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  <Card
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.02)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,215,0,0.08)',
                      borderRadius: '16px',
                      p: 2.5,
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        borderColor: `rgba(255,215,0,0.3)`,
                        boxShadow: `0 8px 30px rgba(255,215,0,0.05)`,
                      },
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: `${feature.color}20`,
                          color: feature.color,
                          width: 56,
                          height: 56,
                        }}
                      >
                        {feature.icon}
                      </Avatar>
                    </Box>
                    <Typography variant="h6" sx={{ color: '#FFFFFF', fontSize: '1rem', fontWeight: 700, mb: 1 }}>
                      {language === 'ar' ? feature.title : feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6B6B6B', fontSize: '0.85rem', lineHeight: 1.6 }}>
                      {language === 'ar' ? feature.description : feature.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Search & Restaurants Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 5 }, px: { xs: 2, sm: 3, md: 4 } }}>
        
        {/* Location Search */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 2, fontWeight: 500 }}>
            {language === 'ar' ? '🔍 ابحث عن مطعم قريب منك' : '🔍 Find a restaurant near you'}
          </Typography>
          
          <Box ref={searchRef} sx={{ position: 'relative', maxWidth: '500px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <input
                  id='area_search'
                  name='area_search'
                  className="search-input"
                  type="text"
                  placeholder={language === "ar" ? keywordsData.search_match_location_place_holder_ar : keywordsData.search_match_location_place_holder_en}
                  value={query}
                  onChange={handleQueryChange}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                />
              </Box>
              <button className="location-btn" onClick={handleAutoDetect}>
                <MyLocationIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </button>
            </Box>

            {showSuggestions && suggestions.length > 0 && (
              <Fade in>
                <Box className="suggestions-dropdown">
                  {suggestions.map((place, index) => (
                    <Box
                      key={index}
                      className="suggestion-item"
                      onClick={() => handleLocationSelect(place)}
                    >
                      <LocationIcon sx={{ fontSize: 18, color: '#FFD700', ml: language === 'ar' ? 1 : 0, mr: language === 'ar' ? 0 : 1 }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>{place}</Typography>
                    </Box>
                  ))}
                </Box>
              </Fade>
            )}
          </Box>
        </Box>
        
        {/* Recently Viewed Stores Section */}
        {recentStores.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WatchLaterIcon sx={{ color: '#FFD700', fontSize: { xs: 20, sm: 24 } }} />
                <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
                  {language === 'ar' ? 'المتاجر التي زرتها مؤخراً' : 'Recently Viewed Stores'}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={clearRecentStores}
                sx={{ color: '#B3B3B3', '&:hover': { color: '#FF3D00' } }}
              >
                {language === 'ar' ? 'مسح الكل' : 'Clear All'}
              </Button>
            </Box>

            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              overflowX: 'auto', 
              pb: 2,
              '&::-webkit-scrollbar': {
                height: 6,
              },
              '&::-webkit-scrollbar-track': {
                background: '#1A1A1A',
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#FFD700',
                borderRadius: 3,
              }
            }}>
              {recentStores.map((store) => (
                <Card
                  key={store.id}
                  onClick={() => navigate(`/restaurant/${store.id}`)}
                  sx={{
                    minWidth: { xs: 140, sm: 160 },
                    maxWidth: { xs: 140, sm: 160 },
                    cursor: 'pointer',
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #2D2D2D',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: '#FFD700',
                    }
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => removeFromRecent(store.id, e)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      zIndex: 2,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: '#B3B3B3',
                      '&:hover': {
                        backgroundColor: '#FF3D00',
                        color: '#FFFFFF'
                      }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  
                  <Box sx={{ p: 1.5 }}>
                    <Avatar
                      src={store.logo}
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 1,
                        border: '2px solid #FFD700',
                        backgroundColor: '#0D0D0D'
                      }}
                    >
                      <StoreIcon sx={{ fontSize: 40, color: '#FFD700' }} />
                    </Avatar>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#FFFFFF', 
                        fontWeight: 600,
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {language === 'ar' ? store.name : store.name_en || store.name}
                    </Typography>
                    {store.rating && (
                      <Chip
                        label={`${store.rating} ⭐`}
                        size="small"
                        sx={{
                          mt: 0.5,
                          backgroundColor: '#FFD700',
                          color: '#000000',
                          height: 20,
                          fontSize: '0.65rem',
                          width: 'fit-content',
                          mx: 'auto'
                        }}
                      />
                    )}
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* Location Header */}
        {location && (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <LocationIcon sx={{ color: '#FFD700', fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'موقعك:' : 'Your location:'}
            </Typography>
            <Chip
              label={location.address}
              size="small"
              sx={{ bgcolor: '#1A1A1A', color: '#FFD700', maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal' } }}
            />
          </Box>
        )}

        {/* Restaurants Grid */}
        {loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={280} sx={{ bgcolor: '#1A1A1A', borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : nearbyRestaurants.length > 0 ? (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {nearbyRestaurants.map((restaurant) => (
              <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
                <RestaurantCard 
                  restaurant={restaurant} 
                  onClick={() => handleRestaurantClick(restaurant)}
                  language={language}
                  isMobile={isMobile}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper sx={{ 
            p: { xs: 4, sm: 6 }, 
            textAlign: 'center', 
            bgcolor: '#0D0D0D',
            border: '2px dashed #2D2D2D',
            borderRadius: 4
          }}>
            <StoreIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#2D2D2D', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#B3B3B3', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {language === 'ar' 
                ? 'لا توجد متاجر في هذه المنطقة بعد' 
                : 'No stores in this area yet'}
            </Typography>
          </Paper>
        )}
      </Container>
      
      <WelcomePopup isOpen={openWelcomeModel} isPart={true} onClose={() => setOpenWelcomeModel(false)} />
      
      {/* Google Maps Modal */}
      <GoogleMapModal
        open={openMap}
        handleClose={() => setOpenMap(false)}
        onLocationSelect={(location) => handleLocationSelect(location)}
        initialCenter={selectedLocation}
        language={language}
        keywordsData={keywordsData}
      />
      <Dialog
  open={videoDialogOpen}
  onClose={handleCloseVideo}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      backgroundColor: '#0D0D0D',
      borderRadius: '16px',
      border: '1px solid #FFD700',
      overflow: 'hidden',
    }
  }}
>
  <DialogTitle sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #2D2D2D',
    p: 2,
  }}>
    <Typography variant="h6" sx={{ color: '#FFD700' }}>
      {language === 'ar' ? 'فيديو توضيحي' : 'Video Tutorial'}
    </Typography>
    <IconButton
      onClick={handleCloseVideo}
      sx={{
        color: '#B3B3B3',
        '&:hover': {
          color: '#FFD700',
        }
      }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  
  <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
    <Box sx={{
      position: 'relative',
      width: '100%',
      paddingTop: '56.25%', // 16:9 aspect ratio
      bgcolor: '#000',
    }}>
      <iframe
        src={videoUrl}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
    </Box>
  </DialogContent>
</Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            backgroundColor: '#0D0D0D',
            color: '#FFFFFF',
            border: `1px solid ${snackbar.severity === 'success' ? '#4CAF50' : snackbar.severity === 'warning' ? '#FF9800' : '#F44336'}`,
            borderRadius: '12px'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Global Styles */}
      <style jsx global>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.2); opacity: 1; }
        }
        
        .search-input {
          width: 100%;
          padding: 12px 16px;
          font-size: 16px;
          background-color: #1A1A1A;
          border: 2px solid #2D2D2D;
          border-radius: 50px;
          color: #FFFFFF;
          outline: none;
          transition: all 0.3s ease;
        }
        
        .search-input:focus {
          border-color: #FFD700;
          box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
        }
        
        .search-input::placeholder {
          color: #6B6B6B;
        }
        
        .location-btn {
          background-color: #FFD700;
          border: none;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }
        
        .location-btn:hover {
          background-color: #FFE55C;
          transform: scale(1.05);
        }
        
        .location-btn svg {
          color: #000000;
        }
        
        .suggestions-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background-color: #1A1A1A;
          border: 1px solid #2D2D2D;
          border-radius: 16px;
          margin-top: 8px;
          max-height: 300px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }
        
        .suggestion-item {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          cursor: pointer;
          transition: background-color 0.2s ease;
          color: #FFFFFF;
        }
        
        .suggestion-item:hover {
          background-color: rgba(255, 215, 0, 0.1);
        }
        
        @media (max-width: 600px) {
          .search-input {
            padding: 10px 14px;
            font-size: 14px;
          }
          
          .location-btn {
            width: 42px;
            height: 42px;
          }
          
          .suggestion-item {
            padding: 10px 14px;
          }
        }
      `}</style>
    </Box>
  );
};

const RestaurantCard = ({ restaurant, onClick, language, isMobile }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const isOpen = restaurant.menu_settings?.is_open ?? true;
  const openStatusText = isOpen 
    ? (language === 'ar' ? 'مفتوح' : 'Open') 
    : (language === 'ar' ? 'مغلق' : 'Closed');
  const openStatusColor = isOpen ? '#4CAF50' : '#FF3D00';

  return (
    <Card 
      onClick={onClick}
      sx={{
        bgcolor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '20px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: '#FFD700',
          boxShadow: '0 8px 24px rgba(255, 215, 0, 0.15)',
        }
      }}
    >
      {/* Status Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          backgroundColor: `${openStatusColor}`,
          padding: '4px 10px',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
          }}
        />
        <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.7rem' }}>
          {openStatusText}
        </Typography>
      </Box>

      {/* Image */}
      <Box sx={{ position: 'relative', height: { xs: 160, sm: 180 }, overflow: 'hidden' }}>
        {!imageLoaded && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#1A1A1A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '2px solid #2D2D2D',
                borderTopColor: '#FFD700',
                animation: 'spin 0.8s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </Box>
        )}
        
        <CardMedia
          component="img"
          image={!imageError ? (restaurant.cover_image || restaurant.logo || noPhoto) : noPhoto}
          alt={restaurant.name}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          sx={{ 
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        />

        {/* Rating Badge */}
        <Chip
          label={`${restaurant.rating || '4.5'} ⭐`}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: '#FFD700',
            color: '#000000',
            fontWeight: 700,
            zIndex: 2
          }}
        />

        {/* Distance Badge */}
        {restaurant.distance && (
          <Chip
            label={`${restaurant.distance.toFixed(1)} km`}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              bgcolor: 'rgba(0,0,0,0.7)',
              color: '#FFD700',
              fontWeight: 600,
              backdropFilter: 'blur(4px)',
              zIndex: 2
            }}
          />
        )}
      </Box>

      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, flex: 1 }}>
        <Typography variant="h6" sx={{ 
          color: '#FFFFFF', 
          fontWeight: 700,
          mb: 0.5,
          fontSize: { xs: '0.95rem', sm: '1rem' },
          lineHeight: 1.3,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {language === 'ar' ? restaurant.name : restaurant.name_en || restaurant.name}
        </Typography>
        
        {restaurant.cuisine_type && (
          <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mb: 1 }}>
            {language === 'ar' ? restaurant.cuisine_type : restaurant.cuisine_type_en || restaurant.cuisine_type}
          </Typography>
        )}
        
        {/* Delivery Info */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimeIcon sx={{ fontSize: 14, color: '#FFD700' }} />
            <Typography variant="caption" sx={{ color: '#FFFFFF' }}>
              {restaurant.delivery_time_min || '25-35'} min
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DeliveryIcon sx={{ fontSize: 14, color: '#FFD700' }} />
            <Typography variant="caption" sx={{ color: '#FFFFFF' }}>
              {restaurant.delivery_fee || '5'} {currencyTrans(restaurant.currency, language)}
            </Typography>
          </Box>
        </Box>

        <Typography variant="caption" sx={{ color: '#6B6B6B', display: 'block' }}>
          {language === 'ar' ? 'الحد الأدنى: ' : 'Min: '}
          {restaurant.min_order_amount || '20'} {currencyTrans(restaurant.currency, language)}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default HomePage;